import { Entity } from "src/app/core/data/schema/entity";
import { SubscriptionInterval, SubscriptionPrice } from "./subscription";
import { PaymentCheckout } from "../ecommerce/transaction";
import { Community } from "../community";

export class CommunitySubscription extends Entity {
    public static className = 'CommunitySubscription';
    static friendlyName = 'Community Subscription';
    public static idField = 'communitySubscriptionId';
    public static currentSubscription: CommunitySubscription;


    subscriptionName: string;
    communitySubscriptionId: number;
    downgradeSubscriptionName: string;
    subscriptionPriceId: number;
    startDate: string;
    expirationDate: string;
    interval: SubscriptionInterval;
    status: SubscriptionStatus;
    cancelledOn: string;
    transactionFee: number;
    owedAmount: number;

    private intervalStr: string;
    private expired = false;

    constructor(communitySubscription?: Partial<CommunitySubscription>) {
        super("CommunitySubscription", "communitySubscriptionId", "subscriptions");
        Object.assign(this, communitySubscription);


        switch (this.interval) {
            case SubscriptionInterval.Monthly:
                this.intervalStr = 'Monthly';
                break;
            case SubscriptionInterval.Yearly:
                this.intervalStr = 'Yearly';
                break;
            case SubscriptionInterval.Daily:
                this.intervalStr = 'Daily';
                break;
        }

        this.expired = this.status === SubscriptionStatus.Expired || new Date(this.expirationDate + 'Z') < new Date();
        this.expirationDate = new Date(this.expirationDate).toDisplayString();
    }

    public static async loadAll() {
        return await CommunitySubscription.fetchArray(CommunitySubscription);
    }

    public static async getCurrent(silent: boolean = true, forceReload: boolean = false) {

        // we at most want to keep for 5 minutes
        const lastSubscription = localStorage.getItem('lastSubscription');
        let wasRecentlyLoaded = false;
        if (lastSubscription != null) {
            const last = new Date(parseInt(lastSubscription));
            const now = new Date();
            const diff = now.getTime() - last.getTime();
            wasRecentlyLoaded = diff < 300000;
        }

        if (wasRecentlyLoaded == false || forceReload || CommunitySubscription.currentSubscription == null) {
            try {
                // we want to keep CommunitySubscription.lastSubscription as the last one we got, but also
                // we don't want to break references, so lets instantiate a new one prior to getting the new one
                if (CommunitySubscription.currentSubscription == null) {
                    CommunitySubscription.currentSubscription = new CommunitySubscription();
                }

                // reset the last subscription
                CommunitySubscription.currentSubscription.downgradeSubscriptionName = null;
                CommunitySubscription.currentSubscription.cancelledOn = null;

                const sub = await CommunitySubscription.getObject(CommunitySubscription, 'subscriptions', 'current', silent);
                if (sub != null) {
                    // we don't want to break any references to it, so we just copy the values over
                    Object.assign(CommunitySubscription.currentSubscription, sub);
                } else {
                    CommunitySubscription.currentSubscription = null;
                }

            } catch (error) {
                CommunitySubscription.currentSubscription = null;
            }

            // store timestamp so we know when we last got the subscription
            localStorage.setItem('lastSubscription', new Date().getTime().toString());
        }

        return CommunitySubscription.currentSubscription;
    }

    public static async unsubscribe() {
        // set that we are saving the community so we don't get an alert
        Community.activeCommunity.didSave = true;
        return await Entity.factory.delete('Unsubscribing', 'subscriptions', 'unsubscribe');
    }

    public static async getBillingPortalUrl() {
        return await Entity.factory.get('Requesting', 'subscriptions', 'billing');
    }

    public static async cancelDowngrade() {
        // set that we are saving the community so we don't get an alert
        Community.activeCommunity.didSave = true;
        return await Entity.factory.delete('Cancelling', 'subscriptions', 'downgrade/cancel');
    }

    public static async cancelUnsubscribe() {
        // set that we are saving the community so we don't get an alert
        Community.activeCommunity.didSave = true;
        return await Entity.factory.get('Resuming', 'subscriptions', 'resume');
    }

    public static async subscribe(price: SubscriptionPrice) {
        // create the request
        const request = {
            subscriptionPriceId: price.subscriptionPriceId
        };
        const response = await Entity.factory.post('Preparing', request as any, 'subscriptions', `subscribe`, 'json', false); // return the transaction token or the checkout url
        // response can be null if we are already subscribed and just modified sub
        if (response == null) {
            return null;
        }
        // otherwise, if not null then it will have the checkout info
        return new PaymentCheckout(response);
    }

    public get intervalString() {
        return this.intervalStr;
    }

    public get isActive() {
        return this.status == SubscriptionStatus.Active && this.expired === false;
    }

    public get isCancelled() {
        return this.status == SubscriptionStatus.Cancelled;
    }

    public get isExpired() {
        return this.expired;
    }

}

export enum SubscriptionStatus {
    Active = 1,
    Cancelled = 2,
    Expired = 3
}